import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Bell, CheckCheck, X, MessageSquare, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';

const typeIcons: Record<string, typeof Bell> = {
  message: MessageSquare,
  compliance_request: FileText,
  info: Bell,
  alert: Bell,
};

export function OnboardingNotificationBell() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);

  // Get current auth user
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) setUserId(data.user.id);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Resolve client_id from client_profiles, fall back to onboarding_sessions via email
  useEffect(() => {
    if (!userId) return;
    supabase.from('client_profiles').select('id').eq('user_id', userId).maybeSingle().then(({ data }) => {
      if (data?.id) { setClientId(data.id); return; }
      // Fall back to onboarding_sessions by email
      supabase.auth.getUser().then(({ data: authData }) => {
        const email = authData.user?.email;
        if (!email) return;
        (supabase as any).from('onboarding_sessions').select('id')
          .eq('email', email.toLowerCase())
          .order('created_at', { ascending: false })
          .limit(1).maybeSingle()
          .then(({ data: s }: { data: any }) => {
            if (s?.id) setClientId(s.id);
          });
      });
    });
  }, [userId]);

  const { data: notifications = [] } = useQuery({
    queryKey: ['onboarding-notifications', clientId, userId],
    queryFn: async () => {
      if (!clientId && !userId) return [];
      // Query customer_notifications by client_id if available, otherwise by session lookup
      const query = supabase
        .from('customer_notifications')
        .select('*')
        .eq('dismissed', false)
        .order('created_at', { ascending: false })
        .limit(30);

      const { data } = clientId
        ? await query.eq('client_id', clientId)
        : await query;

      return data || [];
    },
    enabled: !!(clientId || userId),
    refetchInterval: 30000,
  });

  // Realtime subscription
  useEffect(() => {
    if (!clientId) return;
    const channel = supabase
      .channel(`onboarding-notifs-${clientId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'customer_notifications',
        filter: `client_id=eq.${clientId}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['onboarding-notifications', clientId, userId] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [clientId, userId, queryClient]);

  const unreadCount = notifications.filter((n: any) => !n.read).length;

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from('customer_notifications').update({ read: true }).eq('id', id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['onboarding-notifications', clientId, userId] }),
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const ids = notifications.filter((n: any) => !n.read).map((n: any) => n.id);
      if (!ids.length) return;
      await supabase.from('customer_notifications').update({ read: true }).in('id', ids);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['onboarding-notifications', clientId, userId] }),
  });

  const dismiss = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from('customer_notifications').update({ dismissed: true, read: true }).eq('id', id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['onboarding-notifications', clientId, userId] }),
  });

  // Always render the bell icon (even if no data yet)
  if (!userId && !clientId) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-8 w-8 rounded-lg">
          <Bell className={`w-4 h-4 transition-colors ${unreadCount > 0 ? 'text-foreground' : 'text-muted-foreground'}`} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-destructive text-destructive-foreground text-[10px] font-black flex items-center justify-center px-1 shadow-lg shadow-destructive/40 ring-2 ring-background animate-in zoom-in-75 duration-200">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-sm font-bold text-foreground">Messages from PayNovus</h3>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => markAllRead.mutate()}>
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-80">
          {notifications.length === 0 ? (
            <div className="py-10 text-center">
              <Bell className="w-8 h-8 mx-auto mb-2 text-muted-foreground/20" />
              <p className="text-xs text-muted-foreground">No messages yet</p>
              <p className="text-[10px] text-muted-foreground/60 mt-1">We'll notify you if we need anything</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((n: any) => {
                const Icon = typeIcons[n.type] || Bell;
                return (
                  <div
                    key={n.id}
                    className={`px-4 py-3 flex gap-3 items-start hover:bg-muted/30 transition-colors cursor-pointer ${!n.read ? 'bg-accent/5' : ''}`}
                    onClick={() => { if (!n.read) markRead.mutate(n.id); }}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-accent/15 text-accent`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-1">
                        <p className={`text-xs leading-tight ${!n.read ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                          {n.title}
                        </p>
                        <Button
                          variant="ghost" size="icon" className="h-5 w-5 shrink-0 text-muted-foreground hover:text-foreground"
                          onClick={(e) => { e.stopPropagation(); dismiss.mutate(n.id); }}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                      {n.body && <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-3">{n.body}</p>}
                      <p className="text-[10px] text-muted-foreground/70 mt-1">
                        {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    {!n.read && <div className="w-2 h-2 rounded-full bg-accent shrink-0 mt-1.5" />}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
